import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { TwoFactorService } from './services/two-factor.service';
import { AsideService } from '../aside-container/aside.service';
import { Subscription } from 'rxjs';
import { SnackBarService } from 'app/shared/services/snack-bar.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationModalComponent } from 'app/shared/components/confirmation-modal/confirmation-modal.component';

@Component({ standalone: false,
  selector: 'two-factor',
  templateUrl: './two-factor.component.html',
  styleUrls: ['./two-factor.component.css']
})
export class TwoFactorComponent implements AfterViewInit, OnDestroy {

  public data: any;

  disableGenerate: boolean = false;
  disable2FA: boolean = true;

  isError: boolean = false;
  ErrorMessage: string = '';

  otpAuthUrl: string = '';
  otpAuthSecret: string = '';
  qrValue: string = '';
  userId: number = -1;

  //OTP
  @ViewChildren('F') inputs!: QueryList<ElementRef<HTMLInputElement>>;
  otpValueRecord: string = "";
  otpForm: FormGroup;
  isSubmitting = false;
  otpSubscription: Subscription;

  constructor(
    private fb: FormBuilder,
    private twofactorService: TwoFactorService,
    private asideService: AsideService,
    public snackBar: SnackBarService,
    private dialog: MatDialog,
    private cd: ChangeDetectorRef
  ) {

    this.otpForm = this.fb.group({
      d0: new FormControl(''),
      d1: new FormControl(''),
      d2: new FormControl(''),
      d3: new FormControl(''),
      d4: new FormControl(''),
      d5: new FormControl('')
    });

    this.otpSubscription = this.otpForm.valueChanges.subscribe(() => {
      if (this.digitsLeft === 0 && !this.isSubmitting) {
        this.isSubmitting = true;
        this.confirm();
      }
      if (this.otpValueRecord != this.otpValue) {
        this.isSubmitting = false;
        this.otpValueRecord = this.otpValue;
      }
    });

    if (this.data?.userId) {
      this.userId = this.data.userId;
    }
  }

  ngOnDestroy(): void {
    this.otpSubscription.unsubscribe();
  }
  ngAfterViewInit(): void {
    this.getInfo();
  }

  ngAfterviewInit() {
    if (this.data?.userId) {
      this.userId = this.data.userId;
    }
  }

  getInfo(): void {
    this.twofactorService.getUserInfoTwoFactorAuth().then((response) => {
      this.disable2FA = !response.twoFactorAuthEnabled;
      this.cd.detectChanges();
    }).catch((error) => {
      this.isError = true;
      this.ErrorMessage = 'An error occurred while loading 2FA information.';
      this.cd.detectChanges();
    });
  }

  get digitsLeft(): number {
    const filled = Object.values(this.otpForm.value)
      .filter(v => v && v.toString().length === 1).length;

    return 6 - filled;
  }

  get otpValue(): string {
    return Object.values(this.otpForm.value).join('');
  }

  onKeyDown(event: KeyboardEvent, index: number) {
    const key = event.key;

    if (event.ctrlKey || event.metaKey) {
      return;
    }
    // Allow tab
    if (key === 'Tab')
      return;

    // Arrow navigation
    if (key === 'ArrowRight' && index < 5) {
      event.preventDefault();
      this.focus(index + 1);
      return;
    }

    if (key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      this.focus(index - 1);
      return;
    }

    // Backspace handling
    if (key === 'Backspace' || key === 'Delete') {
      event.preventDefault();

      const control = this.getControl(index);

      if (control.value) {
        control.setValue('');
      } else if (index > 0) {
        this.focus(index - 1);
        this.getControl(index - 1).setValue('');
      }
      return;
    }

    // Only allow digits
    if (!/^\d$/.test(key)) {
      event.preventDefault();
      return;
    }

    // Overwrite behavior
    event.preventDefault();
    this.getControl(index).setValue(key);

    if (index < 5) {
      this.focus(index + 1);
    }
  }

  onInput(event: any, index: number) {
    let value = event.target.value.replace(/\D/g, ''); // Remove non-digit characters
    let maxLength = 6 - index; // if they've clicked/pasted into the middle, we need to adjust the max length to prevent overflow
    event.target.value = value;

    if (value.length !== maxLength && value.length > 0) {
      this.ErrorMessage = 'Invalid OTP format. Please enter a 6-digit code.';
      this.isError = true;
      const control = this.getControl(index);
      control.setValue('');
      return;
    }

    const digits = value.split('');
    for (let i = 0; i < digits.length; i++) {
      const digit = digits[i];
      const control = this.getControl(i + index);
      if (control) {
        control.setValue(digit);
      }
    }

  }

  private getControl(index: number): FormControl {
    return this.otpForm.get(`d${index}`) as FormControl;
  }

  focus(index: number) {
    const input = this.inputs.toArray()[index];
    if (input) {
      input.nativeElement.focus();
    }
  }

  private confirm() {
    this.twofactorService.confirmUserSecret(
      this.userId, this.otpAuthSecret, this.otpValue)
      .then((response) => {
        this.disableGenerate = true;
        this.cd.detectChanges();
        this.snackBar.success('Two-factor authentication has been enabled for your account.');
        this.asideService.close("success");
      })
      .catch((error) => {
        this.isError = true;
        this.ErrorMessage = 'Invalid OTP. Please try again.';
        this.cd.detectChanges();
      });
  }

  generate(): void {
    if (!this.disable2FA) {
      const dialogRef = this.dialog.open(ConfirmationModalComponent, {
        width: '500px',
        // overflow: 'hidden',
        data: {
          title: '2FA Already Enabled.',
          message: 'Generating and Confirming a new QR code will overwrite your existing 2FA setup. Do you wish to continue?'
        }
      });
      dialogRef.componentInstance.onClose.subscribe((result) => {
        if(result.result) {
          this.generateContinue();
        }
      });
    }
    else{
      this.generateContinue();
    }
  }

  generateContinue(): void {
    this.twofactorService.refreshUserSecret().then((response) => {
      console.log(response);
      this.otpAuthUrl = response;
      this.otpAuthSecret = response.split('secret=')[1].split('&')[0];
      this.qrValue = this.otpAuthUrl;
      this.cd.detectChanges(); // required: renders *ngIf="qrValue" and populates @ViewChildren before focus()
      this.focus(0);
    }).catch((error) => {
      this.isError = true;
      this.ErrorMessage = 'An error occurred while generating the QR code. Please try again.';
      this.cd.detectChanges();
    });
  }

  disable(): void {
    this.twofactorService.disableTwoFactorAuth().then((response) => {
      this.snackBar.warning('Two-factor authentication has been disabled.');
      this.asideService.close("success");
    }).catch((error) => {
      this.isError = true;
      if (error.error?.message) {
        this.ErrorMessage = error.error.message;
      } else {
        this.ErrorMessage = 'An error occurred while disabling 2FA.';
      }
      this.cd.detectChanges();
    });
  }

  cancel(): void {
    if (this.isError)
      this.asideService.close("error");
    else
      this.asideService.close("cancel");
  }
}
